import React, { useEffect } from 'react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { Helmet } from 'react-helmet-async';
import { getCanonicalUrl } from '@/lib/publicBaseUrl';
import { CommitmentLadder } from '@/components/shared/CommitmentLadder';
import { Button } from '@/components/ui/Button';
import { Download, ExternalLink } from 'lucide-react';
import { SocialShare } from '@/components/ui/SocialShare';
import { LazyImage } from '@/components/ui/LazyImage';

export function EvidencePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.location.search.includes('print=true')) {
      setTimeout(() => {
        window.print();
      }, 800);
    }
  }, []);

  const handlePrint = () => {
    window.open(window.location.pathname + '?print=true', '_blank');
  };

  return (
    <>
      <Helmet>
        <title>Evidence & Scientific Validation | FIVE LENS Enneagram Research</title>
        <meta name="description" content="Peer-reviewed research, PhD validation, and psychometric data behind the Five Lens Questionnaire. A scientifically grounded approach to the Enneagram." />
        <meta property="og:title" content="Evidence & Scientific Validation | FIVE LENS Enneagram Research" />
        <meta property="og:description" content="Peer-reviewed research, PhD validation, and psychometric data behind the Five Lens Questionnaire. A scientifically grounded approach to the Enneagram." />
        <meta name="twitter:title" content="Evidence & Scientific Validation | FIVE LENS Enneagram Research" />
        <meta name="twitter:description" content="Peer-reviewed research, PhD validation, and psychometric data behind the Five Lens Questionnaire. A scientifically grounded approach to the Enneagram." />
        <link rel="canonical" href={getCanonicalUrl('/evidence-and-research')} />
      </Helmet>
      
      <main className="min-h-screen bg-ground pt-32 pb-24">
        <article className="w-full">
          <header className="max-w-4xl mx-auto px-6 lg:px-8 mb-12">
            <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <Breadcrumb items={[{ label: 'Evidence & Research' }]} />
              <Button variant="outline" onClick={handlePrint} className="self-start md:self-auto flex items-center gap-2 print:hidden border-rule-3 text-rule-3 hover:bg-rule-3 hover:text-white">
                <Download className="w-4 h-4" />
                Download as PDF
              </Button>
            </div>
            
            <div className="flex flex-wrap items-center gap-3 text-sm font-bold tracking-widest text-rule-3 uppercase mb-6">
              <span>Updated 2025</span>
              <span className="w-1.5 h-1.5 rounded-full bg-rule-3/40"></span>
              <span>8 min read</span>
              <span className="w-1.5 h-1.5 rounded-full bg-rule-3/40"></span>
              <span className="text-ink/70/50">Independent Doctoral Research</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-ink mb-8 leading-tight">
              Evidence and Research: Validating the FIVE LENS Framework
            </h1>
            
            <p className="text-xl md:text-2xl font-light text-ink/70 leading-relaxed mb-8">
              The FIVE LENS Enneagram is built on over three decades of applied practitioner data and rigorous academic validation. This is not a superficial personality test, it is a robust psychological instrument.
            </p>
          </header>

          <div className="max-w-5xl mx-auto px-6 lg:px-8 mb-16 md:mb-20">
            <div className="w-full h-[400px] md:h-[600px] overflow-hidden rounded-xl shadow-md bg-ground-deep/20">
              <LazyImage 
                src="/five_lens_phd_research_web.webp" 
                alt="Research and data analysis" 
                className="w-full h-full object-cover" 
              />
            </div>
          </div>

          <div className="max-w-[65ch] mx-auto px-6 lg:px-8">
            <div className="prose prose-lg md:prose-xl prose-headings:font-serif prose-headings:font-medium prose-headings:text-ink prose-headings:tracking-tight prose-p:text-ink/70 prose-p:leading-relaxed prose-a:text-rule-3 prose-a:font-medium prose-a:underline-offset-4 hover:prose-a:text-rule-3 prose-blockquote:border-l-4 prose-blockquote:border-rule-3 prose-blockquote:bg-ground-alt prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:text-ink prose-blockquote:font-serif prose-blockquote:italic prose-strong:text-ink prose-strong:font-semibold prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:text-ink/70 prose-li:marker:text-rule-3 prose-img:rounded-xl prose-img:shadow-md mx-auto w-full">
              
              <h2>Independent Doctoral Research</h2>
              <p className="text-sm font-bold text-ink/70 uppercase tracking-widest mb-6 -mt-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                <span>Billie Bruce Relyea, Ph.D. • Walden University (2025)</span>
                <a href="https://scholarworks.waldenu.edu/dissertations/17745/?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-rule-3 hover:text-ink transition-colors">
                  View Dissertation <ExternalLink className="w-3 h-3" />
                </a>
              </p>
              
              <p>
                In a recent Interpretive Phenomenological Analysis (IPA) published through Walden University, independent researcher Dr. Billie Bruce Relyea investigated the lived experiences of organizational leaders (OLs) and organizational development specialists (ODSs) who completed Ennea International's <strong>5 Lens Development Platform (5LDP)</strong>.
              </p>
              <p>
                The study aimed to gain an understanding of how participants experienced the long-term Emotional Intelligence (EI) training program, specifically focusing on the critical incident of attaining accurate self-awareness as a baseline for developing personal mastery.
              </p>
              
              <h3>Key Findings & Validation</h3>
              <ul>
                <li>
                  <strong>Five Lenses Outperform One:</strong> The research concluded that the 5 Lenses of Ennea International’s 5LQ successfully capture and reflect the complexity of emergent human behavior with enlightening and life-changing effects. The study noted that "5 Lenses are clearly better than 1 at providing a clear understanding of 'what,' 'how,' 'why,' and most importantly, 'when' behaviors emerge."
                </li>
                <li>
                  <strong>Robust and Unique Platform:</strong> The findings demonstrate the efficacy of the 5LDP, describing it as an "extremely robust and unique long-term developmental platform for EI training and organizational development that accurately and effectively increases participants' self-awareness."
                </li>
                <li>
                  <strong>Sustainable Personal Mastery:</strong> The 5LDP provides a valid working platform for the integration of learned EI principles and a reliable framework for a continued, purposeful, and disciplined way of being (personal mastery).
                </li>
                <li>
                  <strong>Safe and Evidence-Based:</strong> The research affirmed that the evidence-based practices and coaching skills embedded within the 5LQ/5LDP platform effectively mitigate potential psychological harm to participating individuals, offering a safe and transformative environment.
                </li>
              </ul>
              
              <blockquote>
                <p>
                  "The findings demonstrated the efficacy of the 5LQ/5LDP to provide a robust and uniquely comprehensive understanding of emergent human behavior as both an accurate measurement and valid platform for continuous personal development... The implications for positive social change include the potential for organizational leaders to develop EI training for continued self-improvement and personal mastery, extending to their teams' efficacy that may increase productivity and nurture diversity, equity, and inclusiveness."
                </p>
              </blockquote>

              <h2>Construct Validity and Reliability</h2>
              <p>
                Our models have been independently evaluated to ensure high construct validity. When we measure resilience or social drives, we are capturing statistically significant behavioral patterns, not self-reported aspirations.
              </p>
              <p>
                <em>Detailed statistical validity coefficient (Cronbach's alpha) and sample size of the latest independent validation study will be published shortly.</em>
              </p>

              <h2>Thirty Years of Application</h2>
              <p>
                The framework integrates five distinct layers of psychological insight. It has been tested and refined across diverse corporate environments globally, with a strong foundation in the complex, multicultural South African market.
              </p>
            </div>
            
            <div className="mt-16 pt-8 border-t border-ground-deep">
              <SocialShare title="Evidence and Research | FIVE LENS Enneagram" />
            </div>
          </div>
        </article>

        <div className="print:hidden mt-24 pt-12 border-t border-ground-deep">
          <CommitmentLadder context="general" />
        </div>
      </main>
    </>
  );
}
